/**
 * Capsule shared view — handles the visitor experience for ?c=<token> links
 */

import { state, setCapsuleMode } from './state.js';
import { validateCapsuleToken, getCapsuleConversation, getHeaders } from './api.js';
import { API_BASE } from './state.js';
import { initInput } from './input.js';
import { initVoice } from './voice.js';
import { sendMessage } from './chat.js';
import {
    initElements,
    clearChat,
    addMessage,
    addSystemMessage,
    hasThinkingContent,
    separateThinkingContent,
    addThinkingToMessage,
    showImageSearchResults,
    addAnalyticsToMessage
} from './ui.js';

/**
 * Initialize capsule mode. Returns true if capsule is valid and initialized.
 */
export async function initCapsuleMode(token, fromUrl = false) {
    const validation = await validateCapsuleToken(token);

    if (!validation.valid) {
        sessionStorage.removeItem('ragsmith_capsule');
        return false;
    }

    // Set capsule state
    setCapsuleMode(token, {
        session_id: validation.session_id,
        allow_prompts: validation.allow_prompts,
        allow_voice: validation.allow_voice,
        agent: validation.agent,
        model: validation.model,
        collection: validation.collection,
        label: validation.label,
    });

    // Persist to sessionStorage for refresh survival
    sessionStorage.setItem('ragsmith_capsule', token);

    // Clean URL
    if (fromUrl) {
        window.history.replaceState({}, '', window.location.pathname);
    }

    // Initialize DOM refs
    initElements();

    // Apply capsule UI restrictions
    applyCapsuleRestrictions(validation);

    // Load and render the conversation
    await loadCapsuleConversation(token, validation);

    // Wire up interactive features if prompting is enabled
    if (validation.allow_prompts) {
        // Initialize plus menu (exposed from main.js)
        window.initPlusMenu?.();
        // Fetch agent settings to populate tool toggles in plus menu
        if (validation.agent) {
            try {
                const agentRes = await fetch(`${API_BASE}/agents/${encodeURIComponent(validation.agent)}`, {
                    headers: getHeaders(false),
                });
                if (agentRes.ok) {
                    const agentData = await agentRes.json();
                    if (agentData.settings) {
                        state.agentSettings = agentData.settings;
                        window.populateToolToggles?.(agentData.settings.tools || [], agentData.settings);
                    }
                }
            } catch (e) {
                console.error('[Capsule] Failed to load agent tools:', e);
            }
        }
        // Initialize input handling (paste, file picker, etc.)
        initInput();
        // Wire up send button using normal chat flow (gets full footers, analytics, tools, etc.)
        const sendBtn = document.getElementById('send-btn');
        const messageInput = document.getElementById('message-input');
        if (sendBtn) sendBtn.addEventListener('click', sendMessage);
        if (messageInput) {
            messageInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                }
            });
        }
        // Initialize voice if allowed
        if (validation.allow_voice) {
            await initVoice();
        }
    }

    return true;
}

/**
 * Apply UI restrictions for capsule mode
 */
function applyCapsuleRestrictions(config) {
    // Hide hamburger / sidebar
    const hamburger = document.getElementById('hamburger-btn');
    if (hamburger) hamburger.style.display = 'none';

    const menuPanel = document.getElementById('menu-panel');
    if (menuPanel) menuPanel.style.display = 'none';

    // Update header
    const headerTitle = document.querySelector('header h1');
    if (headerTitle) {
        headerTitle.textContent = config.label || 'Shared Chat';
        headerTitle.classList.add('capsule-header-title');
    }

    // Hide status indicators
    const statusDot = document.getElementById('status-dot');
    if (statusDot) statusDot.style.display = 'none';

    const headerStatus = document.getElementById('header-status');
    if (headerStatus) headerStatus.style.display = 'none';

    const modeIndicators = document.getElementById('mode-indicators');
    if (modeIndicators) modeIndicators.style.display = 'none';

    // Handle input area based on allow_prompts
    const inputRow = document.getElementById('input-row');
    if (!config.allow_prompts) {
        // Read-only — hide entire input area
        if (inputRow) inputRow.style.display = 'none';
    } else {
        // Interactive — show plus menu but hide capsule-specific items
        const capsuleItem = document.getElementById('plus-menu-capsule');
        if (capsuleItem) capsuleItem.style.display = 'none';
        const capsuleDivider = document.getElementById('plus-menu-capsule-divider');
        if (capsuleDivider) capsuleDivider.style.display = 'none';

        if (!config.allow_voice) {
            const voiceBtn = document.getElementById('voice-btn');
            if (voiceBtn) voiceBtn.style.display = 'none';
        }
    }

    // Hide connecting overlay
    const overlay = document.getElementById('connecting-overlay');
    if (overlay) {
        overlay.style.opacity = '0';
        setTimeout(() => { overlay.style.display = 'none'; }, 300);
    }
}

/**
 * Load and render the capsule conversation
 */
async function loadCapsuleConversation(token, config) {
    try {
        const data = await getCapsuleConversation(token);
        if (!data || !data.messages) {
            addSystemMessage('This conversation is empty.');
            return;
        }

        clearChat();

        // Determine assistant header name: agent → collection → model family → fallback
        let headerName = config.agent || config.collection || null;
        if (headerName && headerName.includes('-')) {
            headerName = headerName.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        }
        if (!headerName && config.model) {
            let modelName = config.model;
            if (modelName.includes('/')) modelName = modelName.split('/')[1] || modelName;
            const match = modelName.match(/^([a-zA-Z]+)/);
            headerName = match
                ? match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase()
                : modelName;
        }
        if (!headerName) headerName = 'Assistant';

        // Messages after capsule creation get full footers (action buttons, analytics)
        const capsuleCreatedAt = data.created_at ? new Date(data.created_at) : null;

        for (const msg of data.messages) {
            // Skip internal system messages
            if (msg.role === 'user' && msg.content.startsWith('[SYSTEM:')) continue;

            // Determine if this message was added after capsule creation
            const msgTime = msg.timestamp ? new Date(msg.timestamp) : null;
            const isNewMessage = capsuleCreatedAt && msgTime && msgTime > capsuleCreatedAt;

            const options = {
                render: true,
                noScroll: true,
                citations: msg.citations || null,
                toolsUsed: msg.tools_used || null,
                stepLog: msg.step_log || null,
                // The share is a copy of the conversation, pictures included.
                // Without these the image that started the conversation was
                // simply absent from the shared page — the bytes are fetched
                // by media id, and the capsule token now authorises that read.
                attachments: msg.attachments || null,
            };

            if (msg.role === 'assistant') {
                options.header = headerName;
                if (isNewMessage && msg.id) {
                    // New messages get full action bar with messageId for delete/redo
                    options.messageId = msg.id;
                } else {
                    // Original messages get playback-only footer
                    options.playbackOnly = true;
                }
            } else if (msg.role === 'user') {
                options.header = 'You';
            }

            // Separate thinking content
            let displayContent = msg.content;
            let thinkingText = null;
            if (msg.role === 'assistant' && hasThinkingContent(msg.content)) {
                const { thinking, response } = separateThinkingContent(msg.content);
                displayContent = response;
                thinkingText = thinking;
            }

            const msgEl = addMessage(msg.role, displayContent, options);

            if (thinkingText && msgEl) {
                addThinkingToMessage(msgEl, thinkingText, true);
            }

            // Render image search results if stored with this message
            if (msg.role === 'assistant' && msg.image_results && msg.image_results.length > 0 && msgEl) {
                showImageSearchResults(msgEl, msg.image_results);
            }

            // Add analytics footer for new assistant messages
            if (isNewMessage && msg.role === 'assistant' && msg.metadata && msgEl) {
                addAnalyticsToMessage(msgEl, msg.metadata);
            }
        }

        // Scroll to top — visitors start reading from the beginning
        const chatContainer = document.getElementById('chat-container');
        if (chatContainer) {
            chatContainer.scrollTop = 0;
        }

    } catch (e) {
        console.error('[Capsule] Failed to load conversation:', e);
        addSystemMessage('Failed to load the shared conversation.');
    }
}
